import { useEffect, useState } from "react";
import type { QccMapSelectionResponse } from "@/lib/api";
import { Building2, ExternalLink, Loader2, LogOut, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiError, landResearchApi } from "@/lib/api";

interface Props {
  open: boolean;
  token: string;
  onClose: () => void;
  onTotalChange: (total: number | null) => void;
  onSelectionChange?: (selection: QccMapSelectionResponse | null) => void;
}

function qccEmbedUrl(entryPath: string): string {
  const configuredOrigin = String(import.meta.env.VITE_QCC_EMBED_ORIGIN ?? "").trim();
  if (configuredOrigin) return new URL(entryPath, configuredOrigin).toString();

  const url = new URL(entryPath, window.location.origin);
  if (window.location.protocol === "http:" && ["5173", "18080"].includes(window.location.port)) {
    url.port = "18000";
  }
  return url.toString();
}

export function QccMapModal({ open, token, onClose, onTotalChange, onSelectionChange }: Props): JSX.Element | null {
  const [reloadKey, setReloadKey] = useState(0);
  const [entryUrl, setEntryUrl] = useState<string | null>(null);
  const [proxySessionId, setProxySessionId] = useState<string | null>(null);
  const [selectionTotal, setSelectionTotal] = useState<number | null>(null);
  const [accountConnected, setAccountConnected] = useState<boolean | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [frameLoading, setFrameLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEntryUrl(null);
    setProxySessionId(null);
    setSelectionTotal(null);
    onSelectionChange?.(null);
    setAccountConnected(null);
    onTotalChange(null);
    setError(null);
    setFrameLoading(true);
    landResearchApi
      .qccMapSession(token)
      .then((session) => {
        if (!cancelled) {
          setProxySessionId(session.session_id);
          setEntryUrl(qccEmbedUrl(session.entry_url));
          setAccountConnected(session.account_connected);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "企查查地图加载失败");
          setFrameLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [onTotalChange, onSelectionChange, open, reloadKey, token]);

  useEffect(() => {
    if (!open || !proxySessionId) return;
    let cancelled = false;
    const refreshTotal = (): void => {
      landResearchApi
        .qccMapSelection(token, proxySessionId)
        .then((selection) => {
          if (cancelled) return;
          setSelectionTotal(selection.total);
          onTotalChange(selection.total);
          onSelectionChange?.(selection);
        })
        .catch(() => {
          // Keep the last captured count during transient polling failures.
        });
    };
    refreshTotal();
    const interval = window.setInterval(refreshTotal, 1_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [onTotalChange, onSelectionChange, open, proxySessionId, token]);

  useEffect(() => {
    if (!open || !proxySessionId) return;
    let cancelled = false;
    const refreshAccount = (): void => {
      landResearchApi
        .qccMapAccount(token)
        .then((account) => {
          if (!cancelled) setAccountConnected(account.connected);
        })
        .catch(() => {
          // A transient status failure must not interrupt the embedded map.
        });
    };
    refreshAccount();
    const interval = window.setInterval(refreshAccount, 2_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [open, proxySessionId, token]);

  useEffect(() => {
    if (!entryUrl || !frameLoading) return;
    const timeout = window.setTimeout(() => setFrameLoading(false), 12_000);
    return () => window.clearTimeout(timeout);
  }, [entryUrl, frameLoading]);

  if (!open) return null;

  const frameIsIsolated = entryUrl
    ? new URL(entryUrl, window.location.origin).origin !== window.location.origin
    : false;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/45 p-3 backdrop-blur-[2px] sm:p-5"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="flex h-[90dvh] w-full max-w-[1320px] flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="企查查附近企业框选"
      >
        <header className="flex min-h-12 items-center justify-between gap-3 border-b border-zinc-200 px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-brand-500" />
            <h2 className="truncate text-[14px] font-semibold text-zinc-900">
              企查查附近企业框选
            </h2>
            {selectionTotal != null ? (
              <span className="shrink-0 text-[13px] font-medium text-brand-600">
                已框选 {selectionTotal.toLocaleString("zh-CN")} 家
              </span>
            ) : null}
            <span className="hidden shrink-0 items-center gap-1.5 text-[12px] text-zinc-500 sm:inline-flex">
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  accountConnected === true
                    ? "bg-emerald-500"
                    : accountConnected === false
                      ? "bg-amber-500"
                      : "bg-zinc-300"
                }`}
              />
              {accountConnected === true
                ? "已登录个人账号"
                : accountConnected === false
                  ? "未登录企查查"
                  : "检测登录状态"}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {accountConnected ? (
              <button
                type="button"
                disabled={disconnecting}
                onClick={() => {
                  setDisconnecting(true);
                  landResearchApi
                    .disconnectQccMapAccount(token)
                    .then(() => {
                      setAccountConnected(false);
                      setReloadKey((value) => value + 1);
                    })
                    .catch((err: unknown) => {
                      setError(err instanceof ApiError ? err.message : "解除企查查账号失败");
                    })
                    .finally(() => setDisconnecting(false));
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 disabled:cursor-wait disabled:opacity-50"
                title="解除企查查账号"
              >
                {disconnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
              </button>
            ) : null}
            <a
              href="https://www.qcc.com/map"
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
              title="在新窗口打开"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
              title="关闭"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="relative min-h-0 flex-1 bg-zinc-100">
          {entryUrl ? (
            <iframe
              key={entryUrl}
              src={entryUrl}
              name={proxySessionId ? `liando-qcc-session:${proxySessionId}` : undefined}
              title="企查查地图"
              className="h-full w-full border-0 bg-white"
              sandbox={`allow-scripts allow-forms allow-modals allow-popups allow-downloads${frameIsIsolated ? " allow-same-origin" : ""}`}
              referrerPolicy="same-origin"
              onLoad={() => setFrameLoading(false)}
            />
          ) : null}

          {frameLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/90 text-sm text-zinc-500">
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                正在加载企查查地图…
              </span>
            </div>
          ) : null}

          {error ? (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-50 p-6">
              <div className="max-w-md text-center">
                <Building2 className="mx-auto h-8 w-8 text-zinc-400" />
                <p className="mt-3 text-sm font-medium text-zinc-800">地图暂时无法加载</p>
                <p className="mt-1 text-xs leading-5 text-zinc-500">{error}</p>
                <Button size="sm" variant="secondary" className="mt-4" onClick={() => setReloadKey((v) => v + 1)}>
                  <RefreshCw className="h-3.5 w-3.5" />
                  重新加载
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
